(() => {
  'use strict';

  const VERSION = '113.1.0';
  const CURRENCY = document.body?.dataset.avCurrency || 'BRL';
  const MEASUREMENT_DEBUG = document.body?.dataset.avMeasurementDebug === 'true';
  const OWNER_VALUES = new Set(['gtm', 'google_app', 'none']);

  /* The query override exists only so the parity harness can capture two owner
     modes in one pass without a human toggling a theme setting between runs.
     It is hard gated on the debug checkbox, which is off in production. */
  const resolveOwner = () => {
    const configured = document.body?.dataset.avMeasurementOwner || 'google_app';
    const safeConfigured = OWNER_VALUES.has(configured) ? configured : 'google_app';
    if (!MEASUREMENT_DEBUG) return safeConfigured;
    try {
      const override = new URLSearchParams(window.location.search).get('av_measurement_owner');
      return OWNER_VALUES.has(override) ? override : safeConfigured;
    } catch (_) { return safeConfigured; }
  };

  const MEASUREMENT_OWNER = resolveOwner();
  const GA4_DATALAYER_ENABLED = MEASUREMENT_OWNER === 'gtm';

  /* Expose the effective owner to automated QA and keep the shared dataLayer
     contract present even when GA4 standard events are intentionally gated. */
  if (document.body) document.body.dataset.avMeasurementOwnerEffective = MEASUREMENT_OWNER;
  window.dataLayer = window.dataLayer || [];
  const STANDARD_EVENTS = new Set([
    'view_item_list',
    'select_item',
    'view_item',
    'add_to_cart',
    'remove_from_cart',
    'view_cart',
    'begin_checkout',
    'purchase',
    'refund',
    'view_promotion',
    'select_promotion'
  ]);
  const CUSTOM_EVENTS = new Set([
    'australia:spoon_view',
    'australia:spoon_play',
    'australia:spoon_select',
    'australia:spoon_product_click',
    'select_moment',
    'select_discovery_preference',
    'view_comparison',
    'open_ingredients',
    'open_nutrition',
    'calculate_shipping',
    'australia:cart_empty_recovery_select',
    'submit_back_in_stock',
    'select_product_format',
    'use_quick_shop',
    'click_article_product',
    'reach_free_shipping_threshold',
    'homepage_find_product_click',
    'homepage_best_sellers_click',
    'select_variant',
    'navigate_to_reviews',
    'navigation_click',
    'search_opened',
    'search_submitted',
    'search_result_clicked',
    'search_zero_results',
    'b2b_cta_clicked',
    'b2b_form_started',
    'b2b_form_submitted',
    'b2b_login_clicked',
    'impact_delivery_proof_view',
    'impact_delivery_method_open',
    'impact_delivery_proof_click',
    'nav_open',
    'nav_close',
    'nav_group_open',
    'nav_link_click',
    'search_open',
    'search_result_click',
    'navigation_exit',
    'pdp_section_open',
    'pdp_section_close',
    'pdp_sticky_cta_view',
    'pdp_sticky_cta_click',
    'pdp_compare_interaction',
    'pdp_choose_other_click',
    'pdp_ingredient_view',
    'pdp_nutrition_view',
    'kit_view',
    'kit_component_click',
    'kit_value_details_open',
    'kit_add_to_cart',
    'kit_unavailable_component',
    'kit_comparison_view',
    'kit_exit_to_single_product',
    'kit_list_impression',
    'kit_pdp_select',
    'kit_pdp_view',
    'kit_add_to_cart_attempt',
    'kit_add_to_cart_success',
    'kit_add_to_cart_error',
    'kit_checkout_start',
    'kit_purchase',
    'kit_refund',
    'kit_stock_blocked_session',
    'kit_experiment_exposure',
    'kit_experiment_select',
    'kit_experiment_add_to_cart',
    'kit_compare_click',
    'kit_notify_submit',
    'journal_index_view',
    'journal_article_view',
    'journal_scroll_depth',
    'journal_read_complete',
    'journal_product_click',
    'journal_related_article_click',
    'journal_pillar_click',
    'journal_recipe_interaction',
    'journal_share',
    'collection_filter_opened',
    'collection_filter_applied',
    'collection_filter_removed',
    'collection_filter_reset',
    'collection_no_results',
    'australia:filter_apply',
    'australia:collection_filter_opened',
    'australia:collection_filter_applied',
    'australia:collection_filter_removed',
    'australia:collection_filter_reset',
    'australia:collection_page_next',
    'australia:collection_no_results',
    'product_card_viewed',
    'product_card_clicked',
    'product_card_add_error',
    'au_collection_hero_link_click',
    'au_product_gallery_change',
    'au_product_card_view',
    'au_product_card_click',
    'au_product_card_quick_add',
    'au_pdp_add_to_cart',
    'details_expanded',
    'category_world_view',
    'au_discovery_box_add',
    'au_discovery_add_to_cart',
    'au_discovery_product_click',
    'au_discovery_complete',
    'au_discovery_step',
    'au_discovery_start',
    'au_track_interaction',
    'au_world_view',
    'portal_click',
    'quick_choice_click',
    'product_click',
    'compare_click',
    'editorial_click',
    'cross_route_click',
    'living_routes_view',
    'living_routes_card_active',
    'living_routes_product_click',
    'living_routes_variant_change',
    'living_routes_add_to_cart',
    'living_routes_add_error',
    'australia:purpose_hero_view',
    'australia:purpose_cta_click',
    'australia:routine_moment_select',
    'australia:world_select',
    'australia:first_choice_start',
    'australia:first_choice_complete',
    'australia:product_purpose_view',
    'australia:standard_link_click',
    'australia:comparison_use',
    'living_routes_category_hero_click',
    'living_routes_journal_click',
    'australia:social_proof_impression',
    'australia:social_proof_filter',
    'australia:customer_story_open',
    'australia:customer_story_product_click',
    'australia:review_cta_click',
    'australia:customer_media_play',
    'australia:horizon_impression',
    'australia:world_enter',
    'australia:world_cta_click',
    'australia:discovery_start',
    'australia:discovery_complete',
    'australia:product_obsession_view',
    'australia:product_obsession_click',
    'australia:proof_method_click',
    'australia:cultural_proof_click',
    'australia:impact_delivery_proof_click',
    'australia:journal_article_view',
    'australia:journal_source_open',
    'australia:press_proof_click',
    'australia:retail_locator_click',
    'australia:store_locator_search',
    'australia:store_locator_filter',
    'australia:store_locator_partner',
    'australia:store_locator_outbound',
    'australia:store_locator_load_more',
    'australia:store_locator_b2b',
    'australia:reviews_section_viewed',
    'australia:reviews_card_advanced',
    'australia:reviews_card_expanded',
    'australia:reviews_product_clicked',
    'australia:reviews_all_clicked',
    'australia:reviews_error',
    'australia:ten_minute_view',
    'australia:ten_minute_jump',
    'australia:ten_minute_complete',
    // Release 6 home (australia_home_release6_spec.md section 14). Added, nothing renamed.
    'home_hero_cta_click',
    'home_panel_view',
    'home_panel_add_to_cart',
    'home_panel_add_error',
    'home_category_click',
    'home_test_cta_click',
    'home_story_click',
    'home_kitchen_click',
    // Release 6.15 (spec 44, change 3). Added, nothing renamed.
    'au_home_kit_click',
    'pdp_multipack_click',
    'pdp_chooser_click',
    'pdp_flavor_click',
    // Release 6.2 product pages (03_Spec_Release_6_2_Product_Pages.md section 15). Added, nothing renamed.
    'pdp_gallery_select',
    'pdp_size_click',
    'pdp_reviews_jump',
    'pdp_faq_open',
    'pdp_next_add',
    'collection_chooser_click',
    'collection_quick_add',
    'collection_kit_add',
    'collection_bag_bar_click',
    'about_video_play',
    'about_quick_add',
    'about_whatsapp_click',
    // Release 6.10 (spec 29, section 6): the Lojas parceiras page, with placement
    'b2b_form_submit',
    'b2b_whatsapp_click',
    'b2b_portal_click',
    // Release 6.12 (spec 33, 3.4): "Ver na lista" on the label test, once per page view, key surface
    'au_chocolate_label_click',
    // Release 6.13 (spec 36, 2.1 H5): a link on the Dá para conferir home band, key card
    'au_conferir_click',
    // Release 6.11 (spec 31, 2.5): the four chapter story on the home, each once per session
    'au_story_view',
    'au_story_chapter',
    'au_story_complete',
    'au_story_cta',
    'impact_record_click',
    'cart_ship_state',
    'cart_gap_add',
    'cart_complement_add',
    'cart_cep_calc',
    'cart_coupon_open',
    'sidecart_open',
    'sidecart_checkout',
    'sidecart_view_cart',
    'restock_click',
    'nav_panel_open',
    'nav_tile_click',
    'nav_hours_click',
    'promise_state',
    'search_chip_click',
    'search_result_click',
    'search_zero',
    'menu_open',
    'menu_account_click',
    // Release 6.18 (spec 53, 2 and 4): a refusal link (row, page) and the test line above the price (product)
    'au_refusal_click',
    'au_test_line_click'
  ]);
  const CUSTOM_KEYS = new Set([
    // Release 6.11 (spec 31, 2.5): au_story_chapter and au_story_cta. Own key
    // names, so collection_quick_add (which sends "chapter") stays as it is.
    'story_chapter',
    'story_cta',
    // Release 6.13: au_conferir_click sends which card (fabrica, rotulo or colher)
    'card',
    // Release 6.15 (spec 44, change 3): au_home_kit_click sends the kit handle (action is already listed)
    'kit',
    'source',
    'moment',
    'preference',
    'discovery_step',
    'comparison_id',
    'disclosure',
    'shipping_result',
    'product_id',
    'product_handle',
    'product_format',
    'article_handle',
    'content_type',
    'list_context',
    'quick_shop_context',
    'variant_id',
    'variant_title',
    'price',
    'currency',
    'cart_source',
    'review_source',
    'item_name',
    'nav_label',
    'nav_location',
    'nav_id',
    'destination',
    'target',
    'result_count',
    'action',
    'method',
    'state',
    'placement',
    'density',
    'menu_group',
    'navigation_location',
    'link_label',
    'label',
    'device_class',
    'current_page_type',
    'taxonomy',
    'section_name',
    'pillar',
    'scroll_depth',
    'kit_handle',
    'kit_status',
    'kit_name',
    'kit_pathway',
    'kit_entry_point',
    'kit_rank',
    'kit_value',
    'kit_component_count',
    'kit_saving_percent',
    'experiment_variant',
    'error_state',
    'filter_group',
    'filter_value',
    'facet',
    'value',
    'results',
    'world',
    'au_world',
    'cart_value',
    'products_added',
    'products_returned',
    'flavour',
    'benefit',
    'occasion',
    'modifier',
    'page_type',
    'template',
    'section_id',
    'module_type',
    'position',
    'variant',
    'collection_handle',
    'media_id',
    'media_position',
    'media_role',
    'card_position',
    'interaction_type',
    'device_type',
    'category',
    'motif',
    'preset',
    'motion_mode',
    'context',
    'viewport_group',
    'living_routes_surface',
    'living_routes_motif',
    'living_routes_preset',
    'living_routes_density',
    'living_routes_motion',
    'page',
    'creative_id',
    'copy_version',
    'component',
    'entry_page',
    'module_version',
    'priority',
    'family',
    'products_compared',
    'source_component',
    'story_id',
    'product_world',
    'routine_moment',
    'source_type',
    'verified',
    'locale',
    'event_alias',
    'proof_id',
    'surface',
    'scope',
    'card_count',
    'index',
    'reason',
    'query_type',
    'selected_category',
    'partner_id',
    'partner_name',
    'partner_type',
    'location_id',
    'visible_before',
    'visible_after',
    'quantity',
    'role',
    'handle',
    'chapter',
    'count',
    'subtotal_band',
    'person',
    'reached',
    'remaining',
    'rate_count',
    'error',
    'item_count',
    'group',
    'input',
    'type',
    'term_length',
    'link',
    // Release 6.18: au_refusal_click sends row (1 to 5) and page; au_test_line_click sends product (handle)
    'row',
    'product'
  ]);
  const LEGACY_EVENT_MAP = Object.freeze({
    av_content_product_click: 'click_article_product',
    pdp_compare_select: 'view_comparison',
    product_compare_click: 'view_comparison',
    av_product_compare: 'view_comparison',
    pdp_delivery_calculated: 'calculate_shipping',
    pdp_oos_notify_submit: 'submit_back_in_stock',
    pdp_review_open: 'navigate_to_reviews',
    av_filter_apply: 'australia:filter_apply',
    av_press_proof_click: 'australia:press_proof_click',
    av_retail_locator_click: 'australia:retail_locator_click'
  });
  const recentEvents = new Map();

  const analyticsAllowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;
      return privacy.analyticsProcessingAllowed() === true;
    } catch (_) {
      return false;
    }
  };

  const text = (value, limit = 120) => String(value ?? '')
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, limit);

  const redactSearchQuery = (value) => text(value, 160)
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[email]')
    .replace(/\b(?:\+?55\s*)?(?:\(?\d{2}\)?[\s.-]?)?(?:9?\d{4})[\s.-]?\d{4}\b/g, '[telefone]')
    .replace(/\b(?:pedido|order|ordem|n[ºo°]?)[\s:#-]*[A-Z0-9-]{5,}\b/gi, '[pedido]')
    .replace(/\b\d{8,}\b/g, '[identificador]')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);

  const number = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const integer = (value, fallback = 0) => Math.max(0, Math.round(number(value, fallback)));
  const money = (cents) => Math.round(number(cents) * 100) / 10000;

  const dedupe = (key, ttl = 1600) => {
    const now = Date.now();
    const previous = recentEvents.get(key) || 0;
    recentEvents.set(key, now);
    recentEvents.forEach((timestamp, eventKey) => {
      if (now - timestamp > 30000) recentEvents.delete(eventKey);
    });
    return now - previous < ttl;
  };

  const stablePayloadValue = (value) => {
    if (Array.isArray(value)) return value.map(stablePayloadValue);
    if (!value || typeof value !== 'object') return value;
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = stablePayloadValue(value[key]);
      return result;
    }, {});
  };

  const push = (event, payload = {}, options = {}) => {
    if (!analyticsAllowed()) return false;
    if (!STANDARD_EVENTS.has(event) && !CUSTOM_EVENTS.has(event)) return false;
    if (STANDARD_EVENTS.has(event) && !GA4_DATALAYER_ENABLED) return false;

    const dedupeKey = options.dedupeKey || `${event}:${JSON.stringify(stablePayloadValue(payload))}`;
    if (dedupeKey && dedupe(dedupeKey, options.ttl || 1600)) return false;

    window.dataLayer = window.dataLayer || [];
    if (payload.ecommerce) window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ...payload, event_version: VERSION });
    window.dispatchEvent(new CustomEvent('av:analytics', { detail: { event, version: VERSION } }));
    return true;
  };

  const itemFromNode = (node, overrides = {}) => {
    const source = node?.closest?.('[data-av-analytics-item]') || node;
    if (!source) return null;
    const data = source.dataset || {};
    const itemId = text(overrides.item_id || data.itemId || data.avVariantId || data.productId, 100);
    const itemName = text(overrides.item_name || data.itemName, 160);
    if (!itemId || !itemName) return null;

    const item = {
      item_id: itemId,
      item_name: itemName,
      item_brand: text(overrides.item_brand || data.itemBrand || document.body?.dataset.avBrand, 80),
      item_category: text(overrides.item_category || data.itemCategory || data.avPdpCategory, 100),
      item_variant: text(overrides.item_variant || data.itemVariant, 120),
      item_list_id: text(overrides.item_list_id || data.itemListId, 100),
      item_list_name: text(overrides.item_list_name || data.itemListName, 120),
      item_format: text(overrides.item_format || data.itemFormat, 80),
      index: integer(overrides.index ?? data.itemIndex, 0),
      price: money(overrides.price_cents ?? data.itemPriceCents),
      quantity: Math.max(1, integer(overrides.quantity ?? data.itemQuantity, 1))
    };
    const discount = money(overrides.discount_cents ?? data.itemDiscountCents);
    if (discount > 0) item.discount = discount;

    Object.keys(item).forEach((key) => {
      if (item[key] === '' || item[key] === 0 && key === 'index') delete item[key];
    });
    return item;
  };

  const ecommercePayload = (items, extra = {}) => {
    const cleanItems = items.filter(Boolean);
    const value = cleanItems.reduce((sum, item) => sum + number(item.price) * Math.max(1, integer(item.quantity, 1)), 0);
    return {
      ecommerce: {
        currency: text(extra.currency || CURRENCY, 3) || 'BRL',
        value: Math.round(value * 100) / 100,
        ...extra,
        items: cleanItems
      }
    };
  };

  const standard = (event, items, extra = {}, options = {}) => {
    if (!STANDARD_EVENTS.has(event)) return false;
    const list = Array.isArray(items) ? items : [items];
    return push(event, ecommercePayload(list, extra), options);
  };

  const custom = (event, payload = {}, options = {}) => {
    if (!CUSTOM_EVENTS.has(event)) return false;
    const safe = {};
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (!CUSTOM_KEYS.has(key) || value === null || value === undefined || value === '') return;
      safe[key] = typeof value === 'number' ? number(value) : text(value, 120);
    });
    return push(event, safe, options);
  };

  const legacy = (event, payload = {}) => {
    const mapped = LEGACY_EVENT_MAP[event] || (CUSTOM_EVENTS.has(event) ? event : '');
    if (!mapped) return false;
    const eventAlias = mapped !== event ? event : '';
    const sent = custom(mapped, {
      event_alias: eventAlias,
      proof_id: payload.proof_id,
      source: payload.source || payload.page_type || payload.template || 'legacy',
      moment: payload.moment,
      placement: payload.placement,
      label: payload.label || payload.link_label,
      product_id: payload.product_id || payload.product,
      product_handle: payload.product_handle || payload.product,
      comparison_id: payload.comparison_id,
      shipping_result: payload.result || payload.destination_state,
      list_context: payload.list_context,
      variant_id: payload.variant_id,
      variant_title: payload.variant_title,
      price: payload.price,
      currency: payload.currency,
      cart_source: payload.cart_source,
      review_source: payload.review_source,
      item_name: payload.item_name,
      world: payload.world,
      au_world: payload.au_world || payload.world,
      link_label: payload.link_label,
      destination: payload.destination,
      collection_handle: payload.collection_handle,
      media_id: payload.media_id,
      media_position: payload.media_position,
      media_role: payload.media_role,
      card_position: payload.card_position,
      interaction_type: payload.interaction_type,
      page: payload.page,
      creative_id: payload.creative_id,
      copy_version: payload.copy_version,
      component: payload.component,
      entry_page: payload.entry_page,
      module_version: payload.module_version,
      priority: payload.priority,
      family: payload.family,
      products_compared: payload.products_compared,
      source_component: payload.source_component,
      facet: payload.facet,
      value: payload.value,
      results: payload.results
    });
    if (sent && eventAlias) {
      window.dispatchEvent(new CustomEvent(eventAlias, {
        detail: {
          canonical_event: mapped,
          placement: text(payload.placement, 40),
          proof_id: text(payload.proof_id, 100),
          facet: text(payload.facet, 100),
          value: text(payload.value, 120),
          results: integer(payload.results, 0),
          version: VERSION
        }
      }));
    }
    return sent;
  };

  const transaction = (event, payload = {}) => {
    if (event !== 'purchase' && event !== 'refund') return false;
    const transactionId = text(payload.transaction_id, 100);
    if (!transactionId) return false;
    const storageKey = `av53:${event}:${transactionId}`;
    try {
      if (localStorage.getItem(storageKey)) return false;
    } catch (_) {}

    const items = Array.isArray(payload.items) ? payload.items.map((item) => ({
      item_id: text(item.item_id, 100),
      item_name: text(item.item_name, 160),
      item_brand: text(item.item_brand || document.body?.dataset.avBrand, 80),
      item_category: text(item.item_category, 100),
      item_variant: text(item.item_variant, 120),
      item_format: text(item.item_format, 80),
      price: Math.round(number(item.price) * 100) / 100,
      quantity: Math.max(1, integer(item.quantity, 1)),
      discount: Math.round(number(item.discount) * 100) / 100
    })).filter((item) => item.item_id && item.item_name) : [];
    if (!items.length) return false;

    const extra = {
      transaction_id: transactionId,
      currency: text(payload.currency || CURRENCY, 3) || 'BRL',
      tax: Math.round(number(payload.tax) * 100) / 100,
      shipping: Math.round(number(payload.shipping) * 100) / 100,
      coupon: text(payload.coupon, 100)
    };
    if (payload.value !== undefined) extra.value = Math.round(number(payload.value) * 100) / 100;
    const sent = standard(event, items, extra, { dedupeKey: storageKey, ttl: 86400000 });
    if (sent) {
      try { localStorage.setItem(storageKey, new Date().toISOString()); } catch (_) {}
      items.filter((item) => text(item.item_category, 100).toLowerCase().includes('kit')).forEach((item) => {
        custom(event === 'purchase' ? 'kit_purchase' : 'kit_refund', {
          source: 'transaction',
          kit_name: item.item_name,
          kit_value: Math.round(item.price * item.quantity * 100) / 100,
          currency: extra.currency
        }, { dedupeKey: `${storageKey}:kit:${item.item_id}`, ttl: 86400000 });
      });
    }
    return sent;
  };

  const itemNodes = (root = document) => [...root.querySelectorAll('[data-av-analytics-item]')]
    .filter((node) => node.dataset.av53State !== 'under_review');

  const trackLists = (root = document) => {
    const groups = new Map();
    itemNodes(root).forEach((node) => {
      if (node.matches('[data-av-pdp-v3], [data-av-supplement-main], [data-av-cart-line]')) return;
      if (node.matches('[data-au-supplement-promotion]')) {
        if (!node.isConnected || !node.getClientRects().length || node.closest('[hidden], [inert], [aria-hidden="true"]')) return;
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return;
      }
      const item = itemFromNode(node);
      if (!item) return;
      const key = item.item_list_id || item.item_list_name || 'storefront';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    groups.forEach((items, key) => {
      standard('view_item_list', items.slice(0, 50), {
        item_list_id: items[0]?.item_list_id || key,
        item_list_name: items[0]?.item_list_name || key
      }, { dedupeKey: `view_item_list:${location.pathname}:${key}`, ttl: 30000 });
    });
  };

  const trackProduct = () => {
    const root = document.querySelector('[data-av-pdp-v3][data-av-analytics-item], [data-av-supplement-main][data-av-analytics-item]');
    if (!root || root.dataset.av53State === 'under_review') return;
    const item = itemFromNode(root);
    if (!item) return;
    standard('view_item', item, {}, { dedupeKey: `view_item:${item.item_id}:${location.pathname}`, ttl: 30000 });
  };

  const trackCart = () => {
    const root = document.querySelector('[data-av-cart-section]');
    if (!root || root.dataset.avCartEmpty === 'true') return;
    const items = [...root.querySelectorAll('[data-av-cart-line][data-av-analytics-item]')].map((node) => itemFromNode(node)).filter(Boolean);
    if (!items.length) return;
    standard('view_cart', items, {}, { dedupeKey: `view_cart:${location.pathname}:${root.dataset.avCartTotalCents}`, ttl: 30000 });

    const threshold = integer(root.dataset.avFreeShippingTargetCents, 0);
    const total = integer(root.dataset.avCartTotalCents, 0);
    if (threshold > 0 && total >= threshold) {
      custom('reach_free_shipping_threshold', { source: 'cart', list_context: 'cart' }, {
        dedupeKey: `reach_free_shipping_threshold:${threshold}:${total}`,
        ttl: 86400000
      });
    }
  };

  const promotionFromNode = (node) => {
    const promo = node?.closest?.('[data-av-promotion]');
    if (!promo) return null;
    return {
      promotion_id: text(promo.dataset.promotionId, 100),
      promotion_name: text(promo.dataset.promotionName, 160),
      creative_name: text(promo.dataset.creativeName, 160),
      creative_slot: text(promo.dataset.creativeSlot, 40)
    };
  };

  const visiblePromotion = (promo) => promo.getAttribute('aria-hidden') !== 'true' && !promo.hidden;
  const trackPromotions = (root = document) => {
    root.querySelectorAll('[data-av-promotion]').forEach((promo) => {
      if (!visiblePromotion(promo)) return;
      const data = promotionFromNode(promo);
      if (!data?.promotion_id || !data.promotion_name) return;
      push('view_promotion', { ecommerce: { items: [data] } }, {
        dedupeKey: `view_promotion:${data.promotion_id}`,
        ttl: 30000
      });
    });
  };

  const initPromotionObservers = (root = document) => {
    trackPromotions(root);
    root.querySelectorAll('[data-av-promotion]').forEach((promo) => {
      if (promo.dataset.avPromotionObserved === 'true') return;
      promo.dataset.avPromotionObserved = 'true';
      new MutationObserver(() => trackPromotions(promo.parentElement || document)).observe(promo, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden', 'hidden']
      });
    });
  };

  const parsePayload = (node) => {
    try { return JSON.parse(node?.dataset.avPayload || '{}'); } catch (_) { return {}; }
  };

  document.addEventListener('click', (event) => {
    const target = event.target.closest('a, button, [data-av-event]');
    const impactCta = event.target.closest('[data-av-impact-cta]');
    if (impactCta) {
      const impactRoot = impactCta.closest('[data-av-impact-view]');
      const impactPayload = {
        source: document.body?.dataset.pageType || 'storefront',
        placement: impactCta.dataset.avImpactPlacement || impactRoot?.dataset.avImpactPlacement,
        density: impactCta.dataset.avImpactDensity || impactRoot?.dataset.avImpactDensity,
        destination: 'impact_page'
      };
      const impactPlacement = impactCta.dataset.avImpactPlacement || 'unknown';
      const impactHref = impactCta.getAttribute('href');
      custom('impact_delivery_proof_click', impactPayload, {
        dedupeKey: `impact_delivery_proof_click:${impactPlacement}:${impactHref}`,
        ttl: 300
      });
      custom('australia:impact_delivery_proof_click', impactPayload, {
        dedupeKey: `australia:impact_delivery_proof_click:${impactPlacement}:${impactHref}`,
        ttl: 300
      });
    }
    if (!target) return;

    const navigationLink = target.closest('[data-av-nav-link], .av-mobile-nav a, .av-mega a');
    if (navigationLink) {
      const inferredLocation = navigationLink.closest('.av-mobile-nav') ? 'mobile-drawer' : navigationLink.closest('.av-mega') ? 'desktop-mega-menu' : 'header';
      const navLocation = navigationLink.dataset.avNavLocation || inferredLocation;
      const navId = navigationLink.dataset.avNavId || '';
      const navHref = navigationLink.getAttribute('href');
      const navDedupeIdentity = navId ? `${navId}:${navHref}` : navHref;
      custom('navigation_click', {
        source: document.body?.dataset.pageType || 'storefront',
        nav_label: navigationLink.dataset.avNavLabel || navigationLink.textContent,
        nav_location: navLocation,
        nav_id: navId || undefined,
        destination: navHref
      }, { dedupeKey: `navigation_click:${navLocation}:${navDedupeIdentity}`, ttl: 300 });
      custom('nav_link_click', {
        navigation_location: navLocation,
        menu_group: navigationLink.closest('[data-av-nav-group-panel]')?.dataset.avNavGroupPanel || navigationLink.closest('[data-av-mobile-group]')?.dataset.avMobileGroup,
        link_label: navigationLink.dataset.avNavLabel || navigationLink.textContent,
        nav_id: navId || undefined,
        destination: navHref,
        taxonomy: navigationLink.dataset.avNavTaxonomy,
        device_class: matchMedia('(max-width: 1279px)').matches ? 'mobile' : 'desktop',
        current_page_type: document.body?.dataset.pageType || 'storefront'
      }, { dedupeKey: `nav_link_click:${navDedupeIdentity}`, ttl: 300 });
      try {
        const destinationUrl = new URL(navigationLink.href, location.href);
        if (destinationUrl.origin !== location.origin) custom('navigation_exit', { destination: destinationUrl.href, link_label: navigationLink.textContent, navigation_location: inferredLocation }, { ttl: 300 });
      } catch (_) {}
    }

    const navGroup = target.closest('[data-av-mega-trigger], [data-av-nav-group-trigger]');
    if (navGroup) {
      custom('nav_group_open', {
        menu_group: navGroup.dataset.avNavGroup || navGroup.closest('[data-av-mobile-group]')?.dataset.avMobileGroup,
        navigation_location: navGroup.matches('[data-av-mega-trigger]') ? 'desktop-header' : 'mobile-drawer',
        device_class: matchMedia('(max-width: 1279px)').matches ? 'mobile' : 'desktop',
        current_page_type: document.body?.dataset.pageType || 'storefront'
      }, { dedupeKey: `nav_group_open:${navGroup.dataset.avNavGroup || navGroup.textContent}`, ttl: 300 });
    }

    if (target.closest('[data-av-mobile-open]')) custom('nav_open', { navigation_location: 'mobile-drawer', device_class: 'mobile' }, { ttl: 300 });
    if (target.closest('[data-av-mobile-close]')) custom('nav_close', { navigation_location: 'mobile-drawer', device_class: 'mobile' }, { ttl: 300 });

    if (target.closest('[data-av-search-open]')) {
      custom('search_opened', { source: document.body?.dataset.pageType || 'storefront' }, {
        dedupeKey: `search_opened:${location.pathname}`,
        ttl: 500
      });
      // Release 6.6 spec 11: the source is where search opened (desktop, phone or the phone menu);
      // search_opened above keeps the page type.
      const searchOpener = target.closest('[data-av-search-open]');
      const searchSource = searchOpener.dataset.auR66SearchSource || (window.matchMedia('(min-width: 1024px)').matches ? 'desktop' : 'phone');
      custom('search_open', { source: searchSource }, { dedupeKey: `search_open:${location.pathname}`, ttl: 500 });
    }

    const searchResult = target.closest('[data-av-search-result-link]');
    if (searchResult) {
      custom('search_result_clicked', {
        source: 'predictive_search',
        // Release 6.6: the link without its query, which carries the search term (_psq)
        destination: (searchResult.getAttribute('href') || '').split('?')[0],
        result_count: document.querySelector('[data-av-search-result-count]')?.dataset.avSearchResultCount
      }, { dedupeKey: `search_result_clicked:${searchResult.getAttribute('href')}`, ttl: 300 });
      // The /search page and the product page cards keep search_result_click as before; the header results
      // send it from assets/au-r66-header.js with the Release 6.6 payload (type, position).
      if (!searchResult.hasAttribute('data-au-r66-result')) custom('search_result_click', { source: 'predictive_search', destination: (searchResult.getAttribute('href') || '').split('?')[0] }, { dedupeKey: `search_result_click:${searchResult.getAttribute('href')}`, ttl: 300 });
    }

    const pillarLink = target.closest('[data-av-journal-pillar]');
    if (pillarLink?.matches('a')) custom('journal_pillar_click', { pillar: pillarLink.dataset.avJournalPillar, destination: pillarLink.getAttribute('href') }, { ttl: 300 });
    const journalProduct = target.closest('.av-article__product');
    if (journalProduct) custom('journal_product_click', { article_handle: document.querySelector('[data-av-journal-article-page]')?.dataset.avJournalArticlePage, destination: journalProduct.getAttribute('href') }, { ttl: 300 });
    const relatedArticle = target.closest('.av-article__recent, [data-av-journal-role="supporting"], [data-av-journal-role="latest"]');
    if (relatedArticle) custom('journal_related_article_click', { article_handle: relatedArticle.dataset.avJournalArticle || '', destination: relatedArticle.getAttribute('href') }, { ttl: 300 });
    if (target.closest('.av-article__share')) custom('journal_share', { article_handle: document.querySelector('[data-av-journal-article-page]')?.dataset.avJournalArticlePage }, { ttl: 300 });

    const stickyCta = target.closest('[data-av-cart-source="sticky"]');
    if (stickyCta) custom('pdp_sticky_cta_click', { product_id: stickyCta.closest('[data-product-id]')?.dataset.productId, source: 'pdp' }, { ttl: 300 });
    const comparisonLink = target.closest('[data-av-comparison] a');
    if (comparisonLink) {
      const comparison = comparisonLink.closest('[data-av-comparison]');
      const comparisonPayload = {
        comparison_id: comparison?.dataset.comparisonId,
        destination: comparisonLink.getAttribute('href'),
        source: 'pdp',
        family: comparison?.dataset.family || '',
        products_compared: comparison?.dataset.productsCompared || ''
      };
      custom('pdp_compare_interaction', comparisonPayload, { ttl: 300 });
      custom('australia:comparison_use', comparisonPayload, {
        dedupeKey: 'comparison_use:' + comparisonPayload.comparison_id + ':' + comparisonPayload.destination,
        ttl: 300
      });
    }

    const promotion = promotionFromNode(target);
    if (promotion && target.closest('[data-av-promotion-link]')) {
      push('select_promotion', { ecommerce: { items: [promotion] } }, {
        dedupeKey: `select_promotion:${promotion.promotion_id}:${promotion.creative_slot}`
      });
    }

    const formatLink = target.closest('[data-av-format-link]');
    if (formatLink) {
      custom('select_product_format', {
        source: 'pdp',
        product_id: formatLink.dataset.productId,
        product_handle: formatLink.dataset.productHandle,
        product_format: formatLink.dataset.productFormat
      });
    }

    const itemNode = target.closest('[data-av-analytics-item]');
    const item = itemFromNode(itemNode);
    if (item && !itemNode.matches('[data-av-supplement-main]') && target.matches('a, a *') && !target.closest('[data-av-format-link], [data-av-event]')) {
      standard('select_item', item, {
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name
      }, { dedupeKey: `select_item:${item.item_id}:${item.item_list_id}` });
    }

    const remove = target.closest('.av-cart__remove');
    const minus = target.closest('[data-av-cart-qty="minus"]');
    const plus = target.closest('[data-av-cart-qty="plus"]');
    if ((remove || minus) && item) {
      const quantity = remove ? item.quantity : 1;
      standard('remove_from_cart', { ...item, quantity }, {}, {
        dedupeKey: `remove_from_cart:${item.item_id}:${quantity}:${Date.now()}`,
        ttl: 0
      });
    }
    if (plus && item) {
      standard('add_to_cart', { ...item, quantity: 1 }, {}, {
        dedupeKey: `add_to_cart:${item.item_id}:cart-plus:${Date.now()}`,
        ttl: 0
      });
    }

    const checkout = target.closest('[name="checkout"], [data-av-mobile-checkout], [data-checkout-button]');
    if (checkout && !checkout.disabled) {
      const cart = checkout.closest('[data-av-cart-section]') || document.querySelector('[data-av-cart-section]');
      const items = cart ? [...cart.querySelectorAll('[data-av-cart-line][data-av-analytics-item]')].map((node) => itemFromNode(node)).filter(Boolean) : [];
      if (items.length) standard('begin_checkout', items, {}, { dedupeKey: `begin_checkout:${cart.dataset.avCartTotalCents}`, ttl: 5000 });
      const kitLine = cart?.querySelector('[data-av-kit-line="true"]');
      if (kitLine) custom('kit_checkout_start', {
        source: 'cart',
        kit_handle: kitLine.dataset.productHandle,
        kit_name: kitLine.dataset.itemName,
        kit_entry_point: 'cart',
        kit_value: integer(cart.dataset.avCartTotalCents, 0) / 100,
        currency: CURRENCY
      }, { dedupeKey: `kit_checkout_start:${cart.dataset.avCartTotalCents}`, ttl: 5000 });
    }

    const tracked = target.closest('[data-av-event]');
    if (tracked) legacy(tracked.dataset.avEvent, parsePayload(tracked));
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-av-cart-input]');
    if (!input) return;
    const line = input.closest('[data-av-cart-line][data-av-analytics-item]');
    const item = itemFromNode(line);
    if (!item) return;
    const next = integer(input.value, item.quantity);
    if (next < item.quantity) {
      standard('remove_from_cart', { ...item, quantity: item.quantity - next }, {}, {
        dedupeKey: `remove_from_cart:${item.item_id}:${item.quantity - next}:${Date.now()}`,
        ttl: 0
      });
    } else if (next > item.quantity) {
      standard('add_to_cart', { ...item, quantity: next - item.quantity }, {}, {
        dedupeKey: `add_to_cart:${item.item_id}:${next - item.quantity}:${Date.now()}`,
        ttl: 0
      });
    }
  }, true);

  document.addEventListener('change', (event) => {
    const target = event.target;
    const root = target.closest?.('[data-av-pdp-v3][data-av-analytics-item], [data-av-supplement-main][data-av-analytics-item]');
    if (!root) return;
    const variantControl = target.matches?.('[name="id"], [data-option-position], select[data-product-select]') || target.closest?.('variant-selects, variant-radios, [data-product-option]');
    if (!variantControl) return;
    window.setTimeout(() => {
      const form = root.querySelector('form[action*="/cart/add"]');
      const variantInput = form?.querySelector('[name="id"]');
      const variantId = text(variantInput?.value || root.dataset.avVariantId, 100);
      const item = itemFromNode(root, { item_id: variantId });
      if (!item) return;
      custom('select_variant', {
        source: 'pdp',
        product_id: root.dataset.productId,
        product_handle: root.dataset.avProductHandle,
        variant_id: variantId,
        variant_title: root.dataset.itemVariant,
        item_name: item.item_name,
        price: item.price,
        currency: CURRENCY
      }, { dedupeKey: `select_variant:${variantId}`, ttl: 500 });
    }, 0);
  }, true);

  document.addEventListener('toggle', (event) => {
    const impactDetails = event.target.closest?.('details[data-av-impact-method]');
    if (impactDetails?.open) {
      const impactRoot = impactDetails.closest('[data-av-impact-view]');
      custom('impact_delivery_method_open', {
        source: document.body?.dataset.pageType || 'storefront',
        placement: impactDetails.dataset.avImpactPlacement || impactRoot?.dataset.avImpactPlacement,
        density: impactRoot?.dataset.avImpactDensity
      });
    }
    const details = event.target.closest?.('details[data-av-decision-disclosure]');
    if (!details) return;
    const disclosure = details.dataset.avDecisionDisclosure;
    const root = details.closest('[data-av-pdp-v3]');
    const payload = {
      source: 'pdp',
      disclosure,
      product_id: root?.dataset.itemId,
      product_handle: root?.dataset.avProductHandle
    };
    custom(details.open ? 'pdp_section_open' : 'pdp_section_close', { ...payload, section_name: disclosure });
    if (!details.open) return;
    if (disclosure === 'nutrition') {
      custom('open_nutrition', payload);
      custom('pdp_nutrition_view', payload);
    } else if (disclosure === 'ingredients' || disclosure === 'may-contain') {
      custom('open_ingredients', payload);
      custom('pdp_ingredient_view', payload);
    }
  }, true);

  document.addEventListener('av:cart:add', (event) => {
    const responseItem = event.detail?.item || {};
    const productId = text(event.detail?.productId || responseItem.product_id, 100);
    if (!productId) return;
    const card = [...document.querySelectorAll('[data-av-card11][data-av-analytics-item]')]
      .find((node) => text(node.dataset.productId, 100) === productId);
    if (!card) return;
    const item = itemFromNode(card, {
      item_id: responseItem.variant_id || responseItem.id,
      item_name: responseItem.product_title || responseItem.title,
      item_variant: responseItem.variant_title,
      price_cents: responseItem.final_price ?? responseItem.price,
      quantity: responseItem.quantity || 1
    });
    if (!item) return;
    standard('add_to_cart', item, {}, {
      dedupeKey: `add_to_cart:${item.item_id}:${item.quantity}:confirmed:${Date.now()}`,
      ttl: 0
    });
  });

  const supplementAddSources = new WeakMap();
  // Record context before AUPurchase's window-capture submit owner stops propagation.
  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-au-purchase-form') || !form.closest('[data-av-supplement-main][data-av-analytics-item]')) return;
    supplementAddSources.set(form, text(form.dataset.avCartSource || 'primary', 32));
  }, true);
  document.addEventListener('australia:purchase_confirmed', (event) => {
    const form = event.target;
    const root = form?.closest?.('[data-av-supplement-main][data-av-analytics-item]');
    if (!root || !(form instanceof HTMLFormElement) || String(event.detail?.productId) !== root.dataset.productId) return;
    const source = supplementAddSources.get(form) || 'primary';
    supplementAddSources.delete(form);
    // AUPurchase already dispatches the standard event after confirmation.
    custom('au_pdp_add_to_cart', {
      source: 'pdp',
      product_id: root.dataset.productId,
      product_handle: root.dataset.avProductHandle,
      variant_id: event.detail.variantId,
      interaction_type: source === 'sticky' ? 'sticky_add' : 'primary_add'
    }, { dedupeKey: `au_pdp_add_to_cart:${event.detail.operationId}`, ttl: 30000 });
  });

  const startedForms = new WeakSet();
  document.addEventListener('focusin', (event) => {
    const form = event.target?.closest?.('form[data-av-form-start-event]');
    if (!form || startedForms.has(form)) return;
    startedForms.add(form);
    legacy(form.dataset.avFormStartEvent, parsePayload(form));
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const backInStock = form.closest('[data-av-back-in-stock]');
    if (backInStock) {
      custom('submit_back_in_stock', {
        source: 'pdp',
        product_id: backInStock.dataset.productId,
        product_handle: backInStock.dataset.productHandle
      });
    }

    if (form.matches('[data-av-event]')) legacy(form.dataset.avEvent, parsePayload(form));

    const action = form.getAttribute('action') || '';
    if (action.includes('/search') || form.getAttribute('role') === 'search') {
      custom('search_submitted', {
        source: form.closest('[data-av-search-panel]') ? 'predictive_search' : 'storefront_search'
      }, { dedupeKey: `search_submitted:${location.pathname}:${Date.now()}`, ttl: 0 });
    }
    if (!action.includes('/cart/add')) return;
    const supplement = form.closest('[data-av-supplement-main]');
    if (supplement?.hasAttribute('data-avs-b2b')) return;
    if (supplement && form.hasAttribute('data-au-purchase-form')) return;
    const cartSource = text(form.dataset.avCartSource || 'primary', 32);
    const itemNode = form.closest('[data-av-analytics-item]') || document.querySelector('[data-av-pdp-v3][data-av-analytics-item], [data-av-supplement-main][data-av-analytics-item]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const variantInput = form.querySelector('[name="id"]');
    const variantLabel = variantInput?.selectedOptions?.[0]?.textContent;
    const item = itemFromNode(itemNode, {
      item_id: variantInput?.value,
      item_variant: variantLabel,
      item_list_id: `pdp_${cartSource}`,
      item_list_name: cartSource === 'sticky' ? 'PDP sticky add to cart' : 'PDP primary purchase',
      quantity: quantityInput?.value || 1
    });
    if (!item) return;

    const cardQuick = form.matches('.av-card11__form') && form.closest('[data-av-card11]');
    const quick = cardQuick || form.matches('[data-avsh-quick-add], [data-au-quick-add]') || form.closest('[data-avsh-quick-add]');
    if (quick) {
      custom('use_quick_shop', {
        source: document.body?.dataset.pageType || 'storefront',
        product_id: item.item_id,
        quick_shop_context: item.item_list_id || 'product_list'
      });
    }
    if (cardQuick) {
      delete form.dataset.avCartSource;
      return;
    }
    custom('au_pdp_add_to_cart', {
      source: 'pdp',
      product_id: itemNode?.dataset.productId,
      product_handle: itemNode?.dataset.productHandle || itemNode?.dataset.avProductHandle,
      variant_id: item.item_id,
      world: itemNode?.dataset.categoryWorld || itemNode?.dataset.avPdpCategory,
      interaction_type: cartSource === 'sticky' ? 'sticky_add' : 'primary_add'
    }, { dedupeKey: `au_pdp_add_to_cart:${item.item_id}:${cartSource}:${Date.now()}`, ttl: 0 });
    standard('add_to_cart', item, {}, { dedupeKey: `add_to_cart:${item.item_id}:${item.quantity}` });
    delete form.dataset.avCartSource;
  }, true);

  document.addEventListener('av:finder-selection', (event) => {
    custom('select_moment', {
      source: event.detail?.source || 'comece-aqui',
      moment: event.detail?.moment
    });
  });

  document.addEventListener('av:discovery-preference', (event) => {
    custom('select_discovery_preference', {
      source: event.detail?.source || 'comece-aqui',
      moment: event.detail?.moment,
      preference: event.detail?.preference,
      discovery_step: event.detail?.discovery_step || 'result'
    });
  });

  document.addEventListener('australia:comece_aqui_change', () => {
    const result = document.querySelector('[data-au-comece-result]');
    if (!result) return;
    requestAnimationFrame(() => { if (result.isConnected) trackLists(result); });
  });

  document.addEventListener('australia:shipping-estimate', (event) => {
    custom('calculate_shipping', {
      source: document.body?.dataset.pageType || 'storefront',
      shipping_result: event.detail?.result || 'unknown'
    });
  });

  const init = (root = document) => {
    trackLists(root);
    trackProduct();
    trackCart();
    initPromotionObservers(root);
    const purposeModules = [
      ...(root.matches?.('[data-av-purpose-impression]') ? [root] : []),
      ...root.querySelectorAll('[data-av-purpose-impression]')
    ];
    purposeModules.forEach((module) => {
      if (module.dataset.avPurposeObserved === 'true' || window.Shopify?.designMode || !analyticsAllowed()) return;
      module.dataset.avPurposeObserved = 'true';
      const fire = () => custom(module.dataset.avPurposeImpression, {
        page: module.dataset.avPage || document.body?.dataset.pageType || 'storefront',
        creative_id: module.dataset.avCreativeId,
        copy_version: module.dataset.avCopyVersion,
        component: module.dataset.avComponent || module.dataset.avCreativeId,
        product_id: module.dataset.avProductId,
        world: module.dataset.avWorld
      }, { dedupeKey: `purpose_impression:${location.pathname}:${module.dataset.avCreativeId}`, ttl: 86400000 });
      if (!('IntersectionObserver' in window)) return;
      let visibleTimer = 0;
      const observer = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
        if (!visible) { clearTimeout(visibleTimer); visibleTimer = 0; return; }
        if (visibleTimer) return;
        visibleTimer = window.setTimeout(() => {
          fire();
          observer.disconnect();
        }, 1000);
      }, { threshold: [0, 0.5, 1] });
      observer.observe(module);
    });
    const journalIndex = root.querySelector('[data-av-journal-index]');
    if (journalIndex) custom('journal_index_view', { source: location.pathname, pillar: journalIndex.dataset.avJournalPillar || 'all' }, { dedupeKey: `journal_index_view:${location.pathname}`, ttl: 30000 });
    const journalArticle = root.querySelector('[data-av-journal-article-page]');
    if (journalArticle) custom('journal_article_view', { article_handle: journalArticle.dataset.avJournalArticlePage, pillar: journalArticle.dataset.avJournalPillar, content_type: journalArticle.dataset.avJournalContentType }, { dedupeKey: `journal_article_view:${location.pathname}`, ttl: 30000 });
    if (journalArticle && journalArticle.dataset.avScrollAnalytics !== 'true') {
      journalArticle.dataset.avScrollAnalytics = 'true';
      const reached = new Set();
      const onScroll = () => {
        const scrollable = Math.max(1, document.documentElement.scrollHeight - innerHeight);
        const depth = Math.min(100, Math.round((scrollY / scrollable) * 100));
        [25, 50, 75, 90].forEach((mark) => {
          if (depth < mark || reached.has(mark)) return;
          reached.add(mark);
          custom('journal_scroll_depth', { article_handle: journalArticle.dataset.avJournalArticlePage, pillar: journalArticle.dataset.avJournalPillar, scroll_depth: mark }, { dedupeKey: `journal_scroll_depth:${location.pathname}:${mark}`, ttl: 86400000 });
          if (mark === 90) custom('journal_read_complete', { article_handle: journalArticle.dataset.avJournalArticlePage, pillar: journalArticle.dataset.avJournalPillar }, { dedupeKey: `journal_read_complete:${location.pathname}`, ttl: 86400000 });
        });
      };
      addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    const sticky = root.querySelector('[data-sticky-add-to-cart]');
    if (sticky && sticky.dataset.avAnalyticsObserved !== 'true' && 'IntersectionObserver' in window) {
      sticky.dataset.avAnalyticsObserved = 'true';
      const stickyObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        custom('pdp_sticky_cta_view', { product_id: sticky.dataset.productId, source: 'pdp' }, { dedupeKey: `pdp_sticky_cta_view:${sticky.dataset.productId}`, ttl: 30000 });
        stickyObserver.disconnect();
      }, { threshold: 0.5 });
      stickyObserver.observe(sticky);
    }
    root.querySelectorAll('[data-predictive-search-results]').forEach((results) => {
      if (results.dataset.avSearchAnalyticsObserved === 'true') return;
      results.dataset.avSearchAnalyticsObserved = 'true';
      new MutationObserver(() => {
        const zeroState = results.querySelector('[data-av-search-zero-results]');
        if (!zeroState) return;
        custom('search_zero_results', {
          source: 'predictive_search',
          result_count: 0
        }, { dedupeKey: `search_zero_results:${location.pathname}:${results.textContent.length}`, ttl: 3000 });
      }).observe(results, { childList: true, subtree: true });
    });

    root.querySelectorAll('[data-av-comparison]').forEach((comparison) => {
      if (comparison.dataset.avComparisonTracked === 'true') return;
      comparison.dataset.avComparisonTracked = 'true';
      const observe = () => custom('view_comparison', {
        source: document.body?.dataset.pageType || 'storefront',
        comparison_id: comparison.dataset.comparisonId,
        product_id: comparison.dataset.productId,
        family: comparison.dataset.family || '',
        products_compared: comparison.dataset.productsCompared || ''
      }, { dedupeKey: `view_comparison:${comparison.dataset.comparisonId}`, ttl: 30000 });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observe();
            observer.disconnect();
          }
        }, { threshold: 0.35 });
        observer.observe(comparison);
      } else observe();
    });

    root.querySelectorAll('[data-av-impact-view][data-av-impact-placement="home_teaser"]').forEach((impact) => {
      if (impact.dataset.avImpactObserved === 'true') return;
      impact.dataset.avImpactObserved = 'true';
      const observe = () => custom('impact_delivery_proof_view', {
        source: document.body?.dataset.pageType || 'storefront',
        placement: impact.dataset.avImpactPlacement,
        density: impact.dataset.avImpactDensity
      }, { dedupeKey: `impact_delivery_proof_view:${location.pathname}:${impact.dataset.avImpactPlacement}`, ttl: 30000 });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observe();
            observer.disconnect();
          }
        }, { threshold: 0.3 });
        observer.observe(impact);
      } else observe();
    });
  };

  const COMMERCE_OS_EVENTS = new Set([
    'australia:subscription_viewed',
    'australia:purchase_option_selected',
    'australia:subscription_frequency_selected',
    'australia:subscription_added_to_cart',
    'australia:subscription_management_clicked',
    'australia:subscription_error',
    'australia:routine_started',
    'australia:routine_step_viewed',
    'australia:routine_option_selected',
    'australia:routine_completed',
    'australia:routine_result_viewed',
    'australia:routine_product_removed',
    'australia:routine_product_clicked',
    'australia:routine_add_all',
    'australia:routine_add_all_success',
    'australia:routine_add_all_error',
    'australia:routine_restarted',
    'australia:proof_cluster_viewed',
    'australia:proof_item_expanded',
    'australia:review_summary_clicked',
    'australia:cross_world_viewed',
    'australia:cross_world_product_clicked',
    'australia:cross_world_added',
    'australia:intent_guide_viewed',
    'australia:intent_product_clicked',
    'australia:related_guide_clicked',
    'australia:predictive_result_clicked',
    'australia:zero_results_viewed',
    'australia:rewards_viewed',
    'australia:rewards_join_clicked',
    'australia:rewards_account_clicked',
    'australia:faq_opened'
  ]);
  const COMMERCE_OS_KEYS = new Set([
    'component', 'source', 'source_entry', 'product_id', 'variant_id',
    'selling_plan_id', 'selling_plan_group_id', 'world', 'worlds', 'step',
    'option', 'options', 'mode', 'result_count', 'result_type', 'position',
    'guide_id', 'proof_id', 'quantity', 'total_cents', 'cart_item_count',
    'plan_name', 'state', 'reason_code', 'error_code', 'question_id'
  ]);

  const commerceValue = (value) => {
    if (Array.isArray(value)) return value.slice(0, 10).map((item) => text(item, 64)).filter(Boolean);
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value === 'boolean') return value;
    return text(value, 96);
  };

  const commerceEventId = () => {
    try {
      return window.crypto?.randomUUID?.() || `av-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    } catch (_) {
      return `av-${Date.now()}`;
    }
  };


  const publishCommerce = (eventName, payload = {}) => {
    try {
      if (document.body?.dataset.avCommerceOs !== 'true' || document.body?.dataset.avCustomEvents !== 'true') return false;
      if (!analyticsAllowed()) return false;
      const normalizedName = text(eventName, 90).startsWith('australia:')
        ? text(eventName, 90)
        : `australia:${text(eventName, 80)}`;
      if (!COMMERCE_OS_EVENTS.has(normalizedName)) return false;

      const safePayload = { schema_version: '2.0' };
      Object.entries(payload || {}).forEach(([key, value]) => {
        if (!COMMERCE_OS_KEYS.has(key) || value === undefined || value === null || value === '') return;
        safePayload[key] = commerceValue(value);
      });
      const dedupeKey = `commerce:${normalizedName}:${JSON.stringify(stablePayloadValue(safePayload))}`;
      if (dedupe(dedupeKey, 1800)) return false;
      safePayload.event_id = commerceEventId();

      let fallbackSent = false;
      const publishFallback = () => {
        if (fallbackSent) return;
        fallbackSent = true;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: normalizedName, ...safePayload });
      };

      const shopifyPublisher = window.Shopify?.analytics?.publish;
      if (typeof shopifyPublisher === 'function') {
        try {
          const result = shopifyPublisher(normalizedName, safePayload);
          if (result && typeof result.catch === 'function') result.catch(publishFallback);
        } catch (_) {
          publishFallback();
        }
      } else {
        publishFallback();
      }
      window.dispatchEvent(new CustomEvent('av:commerce-event', { detail: { event: normalizedName, payload: safePayload } }));
      return true;
    } catch (_) {
      return false;
    }
  };

  window.Australia = window.Australia || {};
  window.Australia.analytics = Object.freeze({ publish: publishCommerce, version: '2.0' });

  window.AVAnalytics = Object.freeze({
    version: VERSION,
    consentAllowed: analyticsAllowed,
    standard,
    custom,
    legacy,
    transaction,
    itemFromNode
  });
  window.AVEvents = Object.freeze({ emit: legacy, version: VERSION });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('visitorConsentCollected', () => init(document));
  if ('MutationObserver' in window && document.body) {
    let impactInitQueued = false;
    new MutationObserver((mutations) => {
      if (impactInitQueued || !mutations.some((mutation) => [...mutation.addedNodes].some((node) => node.nodeType === 1 && (node.matches?.('[data-av-impact-view]') || node.querySelector?.('[data-av-impact-view]'))))) return;
      impactInitQueued = true;
      window.requestAnimationFrame(() => {
        impactInitQueued = false;
        init(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
